import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { useAuth } from '../contexts/AuthContext';
import { scriptDatabaseApi } from '../lib/api';
import { VERTICALS } from '../constants/verticals';
import { CreateFileRequestModal } from '../components/CreateFileRequestModal';
import {
  Plus, Search, Bold, Italic, Underline as UnderlineIcon,
  Strikethrough, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Code2, X, CheckCircle2, Loader2,
  FileText, ChevronDown, ChevronRight, Heading1, Heading2, Heading3,
  Undo2, Redo2, Trash2, Save, GitBranch, Copy, MapPin, Check
} from 'lucide-react';
import { cn } from '../lib/utils';

const ADMIN_LIKE = ['admin', 'team_lead', 'ceo', 'head_media_buying', 'creative_head', 'assistant_team_lead'];

interface Script {
  id: string;
  script_number: string;
  script_title: string;
  vertical: string | null;
  vertical_code: string | null;
  content: string;
  parent_id: string | null;
  script_type?: 'root' | 'variation' | 'state';
  state_code?: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  variation_count?: number;
  state_count?: number;
}

// ── Tiptap toolbar ─────────────────────────────────────────────────────────
function ToolBtn({ onClick, active, title, children, disabled }: {
  onClick: () => void; active?: boolean; title?: string; children: React.ReactNode; disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      className={cn(
        'p-1.5 rounded text-sm transition-colors',
        active ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
        disabled && 'opacity-40 cursor-not-allowed'
      )}
    >{children}</button>
  );
}
function Sep() { return <div className="w-px h-5 bg-gray-200 dark:bg-gray-600 mx-0.5" />; }

function EditorToolbar({ editor, readOnly }: { editor: ReturnType<typeof useEditor>; readOnly: boolean }) {
  if (!editor || readOnly) return null;
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-lg">
      <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}><Undo2 size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}><Redo2 size={15} /></ToolBtn>
      <Sep />
      <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="H1"><Heading1 size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="H2"><Heading2 size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="H3"><Heading3 size={15} /></ToolBtn>
      <Sep />
      <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><UnderlineIcon size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight">
        <span className="text-xs font-bold" style={{ background: 'yellow', padding: '0 2px', borderRadius: 2 }}>H</span>
      </ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Code"><Code2 size={15} /></ToolBtn>
      <Sep />
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Left"><AlignLeft size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Center"><AlignCenter size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Right"><AlignRight size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify size={15} /></ToolBtn>
      <Sep />
      <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote"><Quote size={15} /></ToolBtn>
      <ToolBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block">
        <span className="text-xs font-mono font-bold px-0.5">{'{}'}</span>
      </ToolBtn>
    </div>
  );
}

// ── Script editor / viewer modal ───────────────────────────────────────────
function ScriptEditorModal({
  script, canEdit, canDelete, canCreate, onClose, onSaved, onDelete, onCreateVariation,
}: {
  script: Script;
  canEdit: boolean;
  canDelete: boolean;
  canCreate: boolean;
  onClose: () => void;
  onSaved: (updated: Script) => void;
  onDelete: (id: string) => void;
  onCreateVariation: (parentScript: Script) => void;
}) {
  const [title, setTitle] = useState(script.script_title);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [variations, setVariations] = useState<Script[]>([]);
  const [showVariations, setShowVariations] = useState(false);
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [states, setStates] = useState<Script[]>([]);
  const [showStates, setShowStates] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [newStateCode, setNewStateCode] = useState('');
  const [showAddState, setShowAddState] = useState(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestContent = useRef(script.content || '');
  const latestTitle = useRef(script.script_title);
  const isVariation = !!script.parent_id;

  const editor = useEditor({
    extensions: [StarterKit, Underline, Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }), Link.configure({ openOnClick: false })],
    content: script.content || '',
    editable: canEdit,
    onUpdate: ({ editor: e }) => { latestContent.current = e.getHTML(); scheduleSave(); },
  });

  useEffect(() => () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); }, []);

  // Load variations and states when modal opens (only for root/variation scripts)
  useEffect(() => {
    if (script.script_type === 'state') return;
    setLoadingVariations(true);
    scriptDatabaseApi.getVariations(script.id)
      .then(r => setVariations(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingVariations(false));
  }, [script.id, script.script_type]);

  useEffect(() => {
    if (script.script_type === 'state') return;
    setLoadingStates(true);
    scriptDatabaseApi.getStates(script.id)
      .then(r => setStates(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoadingStates(false));
  }, [script.id, script.script_type]);

  const save = useCallback(async () => {
    setSaveState('saving');
    try {
      const res = await scriptDatabaseApi.update(script.id, {
        script_title: latestTitle.current,
        content: latestContent.current,
      });
      setSaveState('saved');
      onSaved(res.data.data);
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  }, [script.id, onSaved]);

  const scheduleSave = useCallback(() => {
    if (!canEdit) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(save, 1500);
  }, [canEdit, save]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    latestTitle.current = e.target.value;
    scheduleSave();
  };

  const handleDelete = async () => {
    try {
      await scriptDatabaseApi.delete(script.id);
      onDelete(script.id);
      onClose();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleCreateState = async () => {
    if (!newStateCode.trim() || !/^[A-Z]{2}$/.test(newStateCode.trim())) {
      alert('State code must be 2 uppercase letters (e.g., TX, CA)');
      return;
    }
    try {
      const res = await scriptDatabaseApi.createState(script.id, {
        state_code: newStateCode.trim(),
      });
      setStates(prev => [...prev, res.data.data]);
      setNewStateCode('');
      setShowAddState(false);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create state');
    }
  };

  const varCount = Number(script.variation_count) || variations.length;
  const stateCount = Number(script.state_count) || states.length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-8 pb-4 px-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          {/* Script number badge */}
          <span className={cn(
            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold shrink-0',
            isVariation
              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
          )}>
            {isVariation && <GitBranch size={10} className="mr-1" />}
            {script.script_number}
          </span>

          {script.vertical && (
            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 shrink-0">
              {script.vertical}
            </span>
          )}

          <div className="flex-1" />

          {/* Save indicator */}
          {canEdit && (
            <div className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
              {saveState === 'saving' && <><Loader2 size={12} className="animate-spin" /> Saving…</>}
              {saveState === 'saved' && <><CheckCircle2 size={12} className="text-green-500" /> Saved</>}
              {saveState === 'error' && <span className="text-red-500">Save failed</span>}
            </div>
          )}

          {canEdit && (
            <button onClick={save} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors">
              <Save size={13} /> Save
            </button>
          )}

          {/* Create Variation button — shown on root scripts for users who can create */}
          {canCreate && !isVariation && (
            <button
              onClick={() => { onClose(); onCreateVariation(script); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition-colors"
              title="Create a variation of this script"
            >
              <GitBranch size={13} /> New Variation
            </button>
          )}

          {canDelete && (
            confirmDelete ? (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-red-600">Delete?</span>
                <button onClick={handleDelete} className="px-2 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-700">Yes</button>
                <button onClick={() => setConfirmDelete(false)} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-xs">No</button>
              </div>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Delete">
                <Trash2 size={15} />
              </button>
            )
          )}

          <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Title */}
        <div className="px-5 pt-4 pb-2">
          {canEdit ? (
            <input
              type="text"
              value={title}
              onChange={handleTitleChange}
              className="w-full text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-none outline-none placeholder-gray-400 focus:ring-0"
              placeholder="Untitled Script"
            />
          ) : (
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
          )}
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-gray-400">
              {script.created_by_name && `By ${script.created_by_name} · `}
              {new Date(script.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            {isVariation && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 font-medium">
                Variation
              </span>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 mx-5 mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <EditorToolbar editor={editor} readOnly={!canEdit} />
          <div className="min-h-[380px] max-h-[50vh] overflow-y-auto" onClick={() => editor?.chain().focus().run()}>
            <EditorContent editor={editor} className="prose prose-sm dark:prose-invert max-w-none px-6 py-4 focus:outline-none" />
          </div>
        </div>

        {/* Variations panel — root scripts only */}
        {!isVariation && (
          <div className="mx-5 mb-5 border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowVariations(v => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
            >
              {showVariations ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
              <GitBranch size={13} className="text-purple-500" />
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                Variations {loadingVariations ? '' : `(${varCount})`}
              </span>
              {loadingVariations && <Loader2 size={12} className="animate-spin text-gray-400" />}
              {canCreate && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClose(); onCreateVariation(script); }}
                  className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded bg-purple-600 text-white text-[10px] font-medium hover:bg-purple-700 transition-colors"
                >
                  <Plus size={10} /> Add Variation
                </button>
              )}
            </button>

            {showVariations && (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {variations.length === 0 ? (
                  <div className="px-4 py-4 text-center text-xs text-gray-400">
                    No variations yet.{canCreate && ' Click "Add Variation" to create the first one.'}
                  </div>
                ) : (
                  variations.map((v) => (
                    <VariationRow key={v.id} variation={v} canEdit={canEdit} canDelete={canDelete} canCreate={canCreate}
                      onCreateVariation={() => { onClose(); onCreateVariation(script); }}
                      onVariationDeleted={(vid) => setVariations(prev => prev.filter(x => x.id !== vid))}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* States panel — root and variation scripts */}
        {script.script_type !== 'state' && (
          <div className="mx-5 mb-5 border border-gray-100 dark:border-gray-800 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowStates(v => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
            >
              {showStates ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
              <MapPin size={13} className="text-blue-500" />
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                States {loadingStates ? '' : `(${stateCount})`}
              </span>
              {loadingStates && <Loader2 size={12} className="animate-spin text-gray-400" />}
              {canCreate && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAddState(true); }}
                  className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 text-white text-[10px] font-medium hover:bg-blue-700 transition-colors"
                >
                  <Plus size={10} /> Add State
                </button>
              )}
            </button>

            {showStates && (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {states.length === 0 && !showAddState ? (
                  <div className="px-4 py-4 text-center text-xs text-gray-400">
                    No states yet.{canCreate && ' Click "Add State" to create one.'}
                  </div>
                ) : (
                  <>
                    {states.map((s) => (
                      <div key={s.id} className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                          {s.state_code}
                        </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{s.script_title}</span>
                      </div>
                    ))}
                    {showAddState && (
                      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 space-y-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">State Code (2 letters)</label>
                        <input
                          type="text"
                          value={newStateCode}
                          onChange={(e) => setNewStateCode(e.target.value.toUpperCase())}
                          maxLength={2}
                          placeholder="e.g., TX"
                          className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleCreateState}
                            className="flex-1 px-2 py-1.5 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                          >
                            Create
                          </button>
                          <button
                            onClick={() => { setShowAddState(false); setNewStateCode(''); }}
                            className="flex-1 px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Variation row inside the panel ─────────────────────────────────────────
function VariationRow({ variation, canEdit, canDelete, canCreate, onCreateVariation, onVariationDeleted }: {
  variation: Script;
  canEdit: boolean;
  canDelete: boolean;
  canCreate: boolean;
  onCreateVariation: () => void;
  onVariationDeleted: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState<Script | null>(null);

  const handleOpenVariation = async () => {
    try {
      const res = await scriptDatabaseApi.getOne(variation.id);
      setSelectedVariation(res.data.data);
    } catch {
      setSelectedVariation(variation);
    }
  };

  return (
    <>
      <div
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
        onClick={handleOpenVariation}
      >
        <div className="w-4 flex items-center justify-center shrink-0">
          <div className="w-px h-3 bg-gray-300 dark:bg-gray-600" />
        </div>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 shrink-0">
          {variation.script_number}
        </span>
        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate">{variation.script_title}</span>
        <span className="text-[10px] text-gray-400 shrink-0">
          {new Date(variation.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      </div>

      {selectedVariation && (
        <ScriptEditorModal
          script={selectedVariation}
          canEdit={canEdit}
          canDelete={canDelete}
          canCreate={canCreate}
          onClose={() => setSelectedVariation(null)}
          onSaved={(updated) => setSelectedVariation(prev => prev ? { ...prev, ...updated } : prev)}
          onDelete={(id) => { onVariationDeleted(id); setSelectedVariation(null); }}
          onCreateVariation={onCreateVariation}
        />
      )}
    </>
  );
}

// ── HI usage drill-down modal ──────────────────────────────────────────────
// Opens when a user clicks the "Used / Unused" badge on an HI script row.
// Shows every media file + ad-platform campaign where the S<n> reference was
// found, with last-seen timestamps so the team can audit where a script is
// actually live.
function ScriptUsageDrillModal({
  script, summary, onClose,
}: {
  script: Script;
  summary?: { tested: boolean; media_files: number; campaigns: number; last_seen_at: string | null };
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<Array<{
    source_type: 'media_file' | 'campaign';
    source_id: string | null;
    source_name: string | null;
    first_seen_at: string | null;
    last_seen_at: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await scriptDatabaseApi.getScriptUsage(script.script_number);
        if (cancelled) return;
        setEntries(res.data?.data || []);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load usage');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [script.script_number]);

  const mediaFiles = entries.filter(e => e.source_type === 'media_file');
  const campaigns  = entries.filter(e => e.source_type === 'campaign');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-800">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{script.script_number}</span>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Usage drill-down</h2>
              {summary && (
                <span className={cn('ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold',
                  summary.tested
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                )}>{summary.tested ? '✅ Used' : '○ Unused'}</span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{script.script_title}</p>
            {summary && (
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                {summary.media_files} media file{summary.media_files === 1 ? '' : 's'} · {summary.campaigns} campaign{summary.campaigns === 1 ? '' : 's'}
                {summary.last_seen_at && <> · last seen {new Date(summary.last_seen_at).toLocaleString()}</>}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} className="text-gray-400" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="text-center py-12 text-sm text-gray-500"><Loader2 className="inline animate-spin mr-2" size={16} />Loading usage…</div>
          ) : error ? (
            <div className="text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">{error}</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500 italic">
              No references found yet. Run a scan from the page (🔄 Re-scan) and check back.
            </div>
          ) : (
            <>
              {/* Media files */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  📁 Media files ({mediaFiles.length})
                </h3>
                {mediaFiles.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No matching creative-library files.</p>
                ) : (
                  <ul className="space-y-1">
                    {mediaFiles.map((e, i) => (
                      <li key={`mf-${e.source_id || i}`} className="flex items-start justify-between gap-3 px-3 py-2 rounded border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-xs">
                        <span className="font-mono truncate flex-1" title={e.source_name || ''}>{e.source_name || '(unnamed)'}</span>
                        <span className="text-gray-400 flex-shrink-0">
                          {e.last_seen_at ? new Date(e.last_seen_at).toLocaleDateString() : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {/* Campaigns */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                  📈 Ad-platform campaigns ({campaigns.length})
                </h3>
                {campaigns.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No matching campaigns in the ad-platform DB.</p>
                ) : (
                  <ul className="space-y-1">
                    {campaigns.map((e, i) => (
                      <li key={`c-${e.source_id || i}`} className="flex items-start justify-between gap-3 px-3 py-2 rounded border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 text-xs">
                        <span className="font-mono truncate flex-1" title={e.source_name || ''}>{e.source_name || '(unnamed)'}</span>
                        <span className="text-gray-400 flex-shrink-0">
                          {e.last_seen_at ? new Date(e.last_seen_at).toLocaleDateString() : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Create / Variation modal ───────────────────────────────────────────────
function CreateScriptModal({
  onClose, onCreated, codeMap, parentScript,
}: {
  onClose: () => void;
  onCreated: (script: Script) => void;
  codeMap: Record<string, string>;
  parentScript?: Script; // if set → creating a variation
}) {
  const isVariation = !!parentScript;
  const [title, setTitle] = useState(isVariation ? `${parentScript!.script_title} (variation)` : '');
  const [vertical, setVertical] = useState(parentScript?.vertical || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const previewCode = vertical ? codeMap[vertical.toLowerCase()] : undefined;

  const editor = useEditor({
    extensions: [StarterKit, Underline, Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }), Link.configure({ openOnClick: false })],
    content: '',
    editable: true,
  });

  const handleCreate = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (!isVariation && !vertical) { setError('Vertical is required'); return; }
    setSaving(true);
    setError('');
    try {
      let res;
      if (isVariation) {
        res = await scriptDatabaseApi.createVariation(parentScript!.id, {
          script_title: title.trim(),
          content: editor?.getHTML() || '',
        });
      } else {
        res = await scriptDatabaseApi.create({
          script_title: title.trim(),
          vertical,
          content: editor?.getHTML() || '',
        });
      }
      onCreated(res.data.data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-8 pb-4 px-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          {isVariation ? (
            <>
              <GitBranch size={16} className="text-purple-500 shrink-0" />
              <div className="flex-1">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">New Variation</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Variation of{' '}
                  <span className="font-bold text-purple-600 dark:text-purple-400">{parentScript!.script_number}</span>
                  {' · '}{parentScript!.script_title}
                </p>
              </div>
            </>
          ) : (
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex-1">New Script</h2>
          )}
          <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pt-4 pb-2 flex gap-4 items-start">
          <div className="flex-1">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-xl font-bold bg-transparent border-none outline-none placeholder-gray-400 text-gray-900 dark:text-white focus:ring-0"
              placeholder={isVariation ? 'Variation title…' : 'Script Title'}
              autoFocus
            />
          </div>
          {!isVariation && (
            <div className="shrink-0 flex flex-col items-end gap-1">
              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select vertical…</option>
                {VERTICALS.map((v) => {
                  const code = codeMap[v.toLowerCase()];
                  return <option key={v} value={v}>{code ? `[${code}] ${v}` : v}</option>;
                })}
              </select>
              {vertical && (
                <span className="text-xs text-gray-400">
                  Script # will be:{' '}
                  <span className="font-bold text-blue-600 dark:text-blue-400">
                    {previewCode ? `${previewCode}-S?` : '??-S?'}
                  </span>
                  {!previewCode && <span className="ml-1 italic">(code auto-generated)</span>}
                </span>
              )}
            </div>
          )}
          {isVariation && (
            <div className="shrink-0 text-right">
              <div className="text-xs text-gray-400">
                Will be assigned:{' '}
                <span className="font-bold text-purple-600 dark:text-purple-400">
                  {parentScript!.script_number}-v?
                </span>
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{parentScript!.vertical}</div>
            </div>
          )}
        </div>

        {/* Editor */}
        <div className="flex-1 mx-5 mb-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <EditorToolbar editor={editor} readOnly={false} />
          <div className="min-h-[360px] max-h-[55vh] overflow-y-auto">
            <EditorContent editor={editor} className="prose prose-sm dark:prose-invert max-w-none px-6 py-4 focus:outline-none" />
          </div>
        </div>

        {error && <p className="px-5 pb-2 text-sm text-red-600">{error}</p>}

        <div className="px-5 pb-5 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 transition-colors',
              isVariation ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'
            )}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {isVariation ? 'Create Variation' : 'Create Script'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export function ScriptDatabasePage() {
  const { user } = useAuth();
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const allRoles = [user?.role || '', ...additionalRoles];
  const isAdminLike = allRoles.some(r => ADMIN_LIKE.includes(r));
  const isBuyer = user?.role === 'buyer';
  const canCreate = isAdminLike || isBuyer;
  const canDelete = isAdminLike;
  const canEdit = (script: Script) => {
    if (isAdminLike) return true;
    if (isBuyer && script.created_by === user?.id) return true;
    return false;
  };

  const [scripts, setScripts] = useState<Script[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [verticalFilter, setVerticalFilter] = useState('');
  const [selectedScript, setSelectedScript] = useState<Script | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [variationParent, setVariationParent] = useState<Script | null>(null);
  const [verticalCodes, setVerticalCodes] = useState<{ vertical: string; code: string }[]>([]);
  const [showCodes, setShowCodes] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [variationsMap, setVariationsMap] = useState<Record<string, Script[]>>({});
  const [loadingVariations, setLoadingVariations] = useState<Set<string>>(new Set());
  const [statesMap, setStatesMap] = useState<Record<string, Script[]>>({});
  const [loadingStates, setLoadingStates] = useState<Set<string>>(new Set());
  const [raiseRequestForScript, setRaiseRequestForScript] = useState<Script | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  // HI usage map — { 'S5': { tested, media_files, campaigns, last_seen_at, ... } }
  // Populated once on mount; users with no HI access just get an empty map and
  // the badges silently don't render. Used + Unused filter chips read from
  // this map.
  const [hiUsage, setHiUsage] = useState<Record<string, {
    tested: boolean;
    last_tested_at: string | null;
    media_files: number;
    campaigns: number;
    last_seen_at: string | null;
  }>>({});
  const [hiFilter, setHiFilter] = useState<'all' | 'used' | 'unused'>('all');
  const [usageDrillScript, setUsageDrillScript] = useState<Script | null>(null);
  const [scanning, setScanning] = useState(false);

  const codeMap = React.useMemo(() => {
    const m: Record<string, string> = {};
    verticalCodes.forEach(vc => { m[vc.vertical.toLowerCase()] = vc.code; });
    return m;
  }, [verticalCodes]);

  const getCode = (vertical: string | null) => vertical ? codeMap[vertical.toLowerCase()] : undefined;
  const LIMIT = 50;

  const fetchScripts = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const res = await scriptDatabaseApi.getAll({
        search: search || undefined,
        vertical: verticalFilter || undefined,
        page: pg,
        limit: LIMIT,
      });
      setScripts(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch scripts:', err);
    } finally {
      setLoading(false);
    }
  }, [search, verticalFilter]);

  useEffect(() => { setPage(1); fetchScripts(1); }, [search, verticalFilter, fetchScripts]);

  useEffect(() => {
    scriptDatabaseApi.getVerticalCodes()
      .then(res => setVerticalCodes(res.data.data || []))
      .catch(() => {});
  }, []);

  // Load HI usage summary on mount — populates the green "Used" / grey "Unused"
  // badge for every HI script. Silent failure when the user isn't HI-allowed.
  const loadHiUsage = useCallback(async () => {
    try {
      const res = await scriptDatabaseApi.getHiUsageSummary();
      setHiUsage(res.data?.data || {});
    } catch (_) {
      setHiUsage({});
    }
  }, []);
  useEffect(() => { loadHiUsage(); }, [loadHiUsage]);

  // Manual "Re-scan now" button — re-runs the scanner against media files +
  // ad-platform campaigns, then refreshes the usage map so badges update.
  const triggerScan = useCallback(async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const res = await scriptDatabaseApi.scanUsage();
      const d = res.data?.data || {};
      await loadHiUsage();
      const tested   = (d.tested_scripts || []).length;
      const untested = (d.untested_scripts || []).length;
      alert(
        `Scan complete.\n\n` +
        `Files scanned: ${d.scanned_files ?? 0} (${d.files_with_refs ?? 0} with S<n>)\n` +
        `Campaigns scanned: ${d.scanned_campaigns ?? 0} (${d.campaigns_with_refs ?? 0} with S<n>)\n` +
        `Tested: ${tested} · Untested: ${untested}`
      );
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, [scanning, loadHiUsage]);

  const toggleVariations = async (scriptId: string) => {
    const next = new Set(expandedRows);
    if (next.has(scriptId)) {
      next.delete(scriptId);
      setExpandedRows(next);
      return;
    }
    next.add(scriptId);
    setExpandedRows(next);

    // Load variations if not already loaded
    if (!variationsMap[scriptId]) {
      setLoadingVariations(prev => new Set(prev).add(scriptId));
      try {
        const res = await scriptDatabaseApi.getVariations(scriptId);
        setVariationsMap(prev => ({ ...prev, [scriptId]: res.data.data || [] }));
      } catch {}
      finally {
        setLoadingVariations(prev => { const s = new Set(prev); s.delete(scriptId); return s; });
      }
    }

    // Load states if not already loaded
    if (!statesMap[scriptId]) {
      setLoadingStates(prev => new Set(prev).add(scriptId));
      try {
        const res = await scriptDatabaseApi.getStates(scriptId);
        setStatesMap(prev => ({ ...prev, [scriptId]: res.data.data || [] }));
      } catch {}
      finally {
        setLoadingStates(prev => { const s = new Set(prev); s.delete(scriptId); return s; });
      }
    }
  };

  const copyScriptCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCodeId(Math.random().toString()); // trigger visual feedback
      setTimeout(() => setCopiedCodeId(null), 1500);
    });
  };

  const handleScriptClick = async (script: Script) => {
    try {
      const res = await scriptDatabaseApi.getOne(script.id);
      setSelectedScript(res.data.data);
    } catch {
      setSelectedScript(script);
    }
  };

  const handleSaved = (updated: Script) => {
    setScripts(prev => prev.map(s => s.id === updated.id ? { ...s, ...updated } : s));
    if (selectedScript?.id === updated.id) setSelectedScript(prev => prev ? { ...prev, ...updated } : prev);
  };

  const handleCreated = (script: Script) => {
    if (script.parent_id) {
      // It's a variation — add to the parent's variations list and update count
      setVariationsMap(prev => ({
        ...prev,
        [script.parent_id!]: [...(prev[script.parent_id!] || []), script],
      }));
      setScripts(prev => prev.map(s =>
        s.id === script.parent_id
          ? { ...s, variation_count: (Number(s.variation_count) || 0) + 1 }
          : s
      ));
      // Expand the parent row
      setExpandedRows(prev => new Set(prev).add(script.parent_id!));
    } else if (script.script_type === 'state') {
      // It's a state — add to parent's states list and update count
      const parentId = script.parent_id!;
      setStatesMap(prev => ({
        ...prev,
        [parentId]: [...(prev[parentId] || []), script],
      }));
      setScripts(prev => prev.map(s =>
        s.id === parentId
          ? { ...s, state_count: (Number(s.state_count) || 0) + 1 }
          : s
      ));
    } else {
      setScripts(prev => [script, ...prev]);
      setTotal(t => t + 1);
    }
    scriptDatabaseApi.getVerticalCodes()
      .then(res => setVerticalCodes(res.data.data || []))
      .catch(() => {});
  };

  const handleDelete = (id: string) => {
    setScripts(prev => prev.filter(s => s.id !== id));
    setTotal(t => t - 1);
    // Also remove from any variation lists
    setVariationsMap(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => {
        next[k] = next[k].filter(v => v.id !== id);
      });
      return next;
    });
  };

  const handleCreateVariation = (parent: Script) => {
    setVariationParent(parent);
    setShowCreate(true);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText size={20} className="text-blue-500" />
            Script Database
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{total} script{total !== 1 ? 's' : ''}</p>
        </div>
        {canCreate && (
          <button
            onClick={() => { setVariationParent(null); setShowCreate(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} /> New Script
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3 px-6 py-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search scripts…"
              className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setVerticalFilter('')}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border',
                verticalFilter === ''
                  ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
            >
              All
            </button>
            {verticalCodes.map((vc) => (
              <button
                key={vc.vertical}
                onClick={() => setVerticalFilter(vc.vertical)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border',
                  verticalFilter === vc.vertical
                    ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                )}
              >
                [{vc.code}] {vc.vertical}
              </button>
            ))}
          </div>
          {/* HI usage filter chips — only meaningful when viewing HI (or All).
              Counts come straight from the loaded usage map. */}
          {(verticalFilter === '' || /home insurance/i.test(verticalFilter)) && Object.keys(hiUsage).length > 0 && (
            <div className="flex items-center gap-1 pl-2 border-l border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setHiFilter('all')}
                className={cn('px-2 py-1 text-[11px] font-medium rounded',
                  hiFilter === 'all' ? 'bg-gray-200 dark:bg-gray-700' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
                title="Show every script regardless of usage"
              >All</button>
              <button
                onClick={() => setHiFilter('used')}
                className={cn('px-2 py-1 text-[11px] font-medium rounded',
                  hiFilter === 'used' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
                title="Only scripts that appear in HI media files or campaigns"
              >✅ Used ({Object.values(hiUsage).filter(u => u.tested).length})</button>
              <button
                onClick={() => setHiFilter('unused')}
                className={cn('px-2 py-1 text-[11px] font-medium rounded',
                  hiFilter === 'unused' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
                title="HI scripts not yet referenced in any media file or campaign"
              >○ Unused ({Object.values(hiUsage).filter(u => !u.tested).length})</button>
              <button
                onClick={triggerScan}
                disabled={scanning}
                className="px-2 py-1 text-[11px] font-medium rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                title="Re-run the HI usage scan (media files + ad-platform campaigns) and refresh the badges"
              >{scanning ? '⏳ Scanning…' : '🔄 Re-scan'}</button>
            </div>
          )}
          <button
            onClick={() => setShowCodes(s => !s)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
              showCodes
                ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            )}
          >
            <ChevronDown size={13} className={cn('transition-transform', showCodes && 'rotate-180')} />
            Vertical Codes {verticalCodes.length > 0 && `(${verticalCodes.length})`}
          </button>
        </div>

        {showCodes && (
          <div className="px-6 pb-3">
            <div className="rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3">
              {verticalCodes.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No vertical codes yet. Generated automatically on first script.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {verticalCodes.map(vc => (
                    <div key={vc.vertical} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-xs">
                      <span className="font-bold text-blue-600 dark:text-blue-400">{vc.code}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-600 dark:text-gray-300">{vc.vertical}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
        ) : scripts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <FileText size={40} className="mb-3 opacity-30" />
            <p className="text-sm">No scripts found</p>
            {canCreate && (
              <button onClick={() => { setVariationParent(null); setShowCreate(true); }} className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors">
                <Plus size={14} /> Create your first script
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                  <th className="w-8 px-2 py-3" />
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Script #</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide flex-1">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-36">Vertical</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-32">Created By</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-24">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-32">Status</th>
                  <th className="w-56" />
                </tr>
              </thead>
              <tbody>
                {scripts
                  .filter(script => {
                    // HI usage filter only applies to HI rows; non-HI scripts pass through.
                    if (hiFilter === 'all') return true;
                    const isHi = /home insurance/i.test(script.vertical || '');
                    if (!isHi) return true;
                    const u = hiUsage[script.script_number];
                    if (!u) return hiFilter === 'unused'; // missing usage row = treat as unused
                    return hiFilter === 'used' ? u.tested : !u.tested;
                  })
                  .map((script, idx) => {
                  const varCount = Number(script.variation_count) || 0;
                  const isExpanded = expandedRows.has(script.id);
                  const loadingVars = loadingVariations.has(script.id);
                  const vars = variationsMap[script.id] || [];

                  return (
                    <React.Fragment key={script.id}>
                      {/* Root script row */}
                      <tr
                        onClick={() => handleScriptClick(script)}
                        className={cn(
                          'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors',
                          idx !== 0 && 'border-t border-gray-100 dark:border-gray-800'
                        )}
                      >
                        {/* Expand chevron - only if has variations or states */}
                        <td className="px-1 py-3" onClick={(e) => e.stopPropagation()}>
                          {((varCount > 0) || (Number(script.state_count) > 0)) && (
                            <button
                              onClick={() => toggleVariations(script.id)}
                              className="p-1 rounded text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          )}
                        </td>
                        {/* Script number - clickable to copy */}
                        <td className="px-4 py-3" onClick={(e) => { e.stopPropagation(); copyScriptCode(script.script_number); }}>
                          <button
                            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:opacity-80 transition-opacity"
                            title="Click to copy"
                          >
                            {script.script_number}
                            {copiedCodeId === script.id && <Check size={10} />}
                          </button>
                        </td>
                        {/* Title */}
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900 dark:text-white">{script.script_title}</span>
                        </td>
                        {/* Vertical */}
                        <td className="px-4 py-3">
                          {script.vertical ? (
                            <div className="flex items-center gap-1.5">
                              {(script.vertical_code || getCode(script.vertical)) && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 font-mono">
                                  {script.vertical_code || getCode(script.vertical)}
                                </span>
                              )}
                              <span className="text-gray-600 dark:text-gray-400 text-sm">{script.vertical}</span>
                            </div>
                          ) : '—'}
                        </td>
                        {/* Created By */}
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-sm">{script.created_by_name || '—'}</td>
                        {/* Date */}
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-500 text-xs">
                          {new Date(script.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        {/* Status — HI-only usage badge. Click opens the drill-down modal. */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const isHi = /home insurance/i.test(script.vertical || '');
                            if (!isHi) return <span className="text-[10px] text-gray-400">—</span>;
                            const u = hiUsage[script.script_number];
                            const used = u?.tested;
                            const total = (u?.media_files || 0) + (u?.campaigns || 0);
                            const tip = u
                              ? `${u.media_files} file${u.media_files === 1 ? '' : 's'} · ${u.campaigns} campaign${u.campaigns === 1 ? '' : 's'}` +
                                (u.last_seen_at ? ` · last seen ${new Date(u.last_seen_at).toLocaleDateString()}` : '')
                              : 'No usage data yet — click Re-scan above';
                            return (
                              <button
                                onClick={() => setUsageDrillScript(script)}
                                title={`${tip}. Click to see where.`}
                                className={cn(
                                  'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition-opacity hover:opacity-80',
                                  used
                                    ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700'
                                    : 'bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600'
                                )}
                              >
                                {used ? `✅ Used ${total > 0 ? `${total}×` : ''}` : '○ Unused'}
                              </button>
                            );
                          })()}
                        </td>
                        {/* Actions */}
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            {canCreate && (
                              <button
                                onClick={() => handleCreateVariation(script)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition-colors"
                                title="Create a variation"
                              >
                                <GitBranch size={13} />
                                Add Variation
                              </button>
                            )}
                            <button
                              onClick={() => setRaiseRequestForScript(script)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors"
                              title="Raise a file request with this script"
                            >
                              <FileText size={13} />
                              Raise FR
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Variation rows */}
                      {isExpanded && vars.map((v) => (
                        <tr
                          key={v.id}
                          onClick={() => handleScriptClick(v)}
                          className="cursor-pointer bg-purple-50/40 dark:bg-purple-900/5 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors border-t border-purple-100/60 dark:border-purple-900/20"
                        >
                          <td className="pl-8 pr-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex flex-col items-center shrink-0">
                                <div className="w-px h-2 bg-purple-200 dark:bg-purple-800" />
                                <div className="w-3 h-px bg-purple-200 dark:bg-purple-800" />
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                                {v.script_number}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{v.script_title}</span>
                              {Number(v.state_count) > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {(statesMap[v.id] || []).length > 0 && statesMap[v.id].map(s => (
                                    <span key={s.id} className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors" onClick={(e) => { e.stopPropagation(); handleScriptClick(s); }} title={`Click to view state ${s.state_code}`}>
                                      {s.state_code}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-500 text-xs">{v.vertical || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-500 text-xs">{v.created_by_name || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-500 text-xs">
                            {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          {/* Status — variations inherit their root's tested flag (the scanner propagates) */}
                          <td className="px-4 py-2.5 text-[10px] text-gray-400">inherits root</td>
                          <td />
                        </tr>
                      ))}

                      {/* State rows */}
                      {isExpanded && (statesMap[script.id] || []).map((state) => (
                        <tr
                          key={state.id}
                          onClick={() => handleScriptClick(state)}
                          className="cursor-pointer bg-blue-50/40 dark:bg-blue-900/5 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors border-t border-blue-100/60 dark:border-blue-900/20"
                        >
                          <td className="pl-8 pr-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <div className="flex flex-col items-center shrink-0">
                                <div className="w-px h-2 bg-blue-200 dark:bg-blue-800" />
                                <div className="w-3 h-px bg-blue-200 dark:bg-blue-800" />
                              </div>
                              <MapPin size={12} className="text-blue-500 dark:text-blue-400" />
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                                {state.state_code}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex flex-col gap-1">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{state.script_title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-500 text-xs">{state.vertical || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-500 text-xs">{state.created_by_name || '—'}</td>
                          <td className="px-4 py-2.5 text-gray-500 dark:text-gray-500 text-xs">
                            {new Date(state.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td className="px-4 py-2.5 text-[10px] text-gray-400">inherits root</td>
                          <td />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800">
                <span className="text-xs text-gray-500">{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total}</span>
                <div className="flex gap-2">
                  <button disabled={page === 1} onClick={() => { setPage(p => p - 1); fetchScripts(page - 1); }} className="px-3 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">Prev</button>
                  <button disabled={page * LIMIT >= total} onClick={() => { setPage(p => p + 1); fetchScripts(page + 1); }} className="px-3 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {usageDrillScript && (
        <ScriptUsageDrillModal
          script={usageDrillScript}
          summary={hiUsage[usageDrillScript.script_number]}
          onClose={() => setUsageDrillScript(null)}
        />
      )}
      {selectedScript && (
        <ScriptEditorModal
          script={selectedScript}
          canEdit={canEdit(selectedScript)}
          canDelete={canDelete}
          canCreate={canCreate}
          onClose={() => setSelectedScript(null)}
          onSaved={handleSaved}
          onDelete={handleDelete}
          onCreateVariation={handleCreateVariation}
        />
      )}

      {showCreate && (
        <CreateScriptModal
          onClose={() => { setShowCreate(false); setVariationParent(null); }}
          onCreated={handleCreated}
          codeMap={codeMap}
          parentScript={variationParent || undefined}
        />
      )}

      {raiseRequestForScript && (
        <CreateFileRequestModal
          onClose={() => setRaiseRequestForScript(null)}
          onSuccess={() => setRaiseRequestForScript(null)}
          initialData={{
            verticals: raiseRequestForScript.vertical ? [raiseRequestForScript.vertical] : [],
            canvasHeadline: raiseRequestForScript.script_number,
          }}
        />
      )}

      {/* Tiptap prose styles */}
      <style>{`
        .ProseMirror { outline: none; min-height: 300px; }
        .ProseMirror p { margin: 0.5em 0; }
        .ProseMirror h1 { font-size: 1.75em; font-weight: 700; margin: 0.75em 0 0.25em; }
        .ProseMirror h2 { font-size: 1.4em; font-weight: 600; margin: 0.75em 0 0.25em; }
        .ProseMirror h3 { font-size: 1.15em; font-weight: 600; margin: 0.75em 0 0.25em; }
        .ProseMirror ul { list-style: disc; padding-left: 1.5em; }
        .ProseMirror ol { list-style: decimal; padding-left: 1.5em; }
        .ProseMirror li { margin: 0.2em 0; }
        .ProseMirror blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; color: #6b7280; margin: 0.75em 0; }
        .ProseMirror code { background: #f3f4f6; border-radius: 3px; padding: 0.1em 0.3em; font-family: monospace; font-size: 0.875em; }
        .ProseMirror pre { background: #1f2937; color: #f9fafb; padding: 1em; border-radius: 6px; overflow-x: auto; margin: 0.75em 0; }
        .ProseMirror pre code { background: none; color: inherit; padding: 0; }
        .dark .ProseMirror code { background: #374151; color: #f9fafb; }
        .dark .ProseMirror blockquote { border-left-color: #4b5563; color: #9ca3af; }
        .ProseMirror mark { background-color: #fef08a; border-radius: 2px; padding: 0 1px; }
        .dark .ProseMirror mark { background-color: #713f12; color: #fef3c7; }
        .ProseMirror a { color: #2563eb; text-decoration: underline; }
      `}</style>
    </div>
  );
}
